import { useCallback } from 'react';

import { normalizeId } from '../utils/ids';

/**
 * ONE ITEM UP OR DOWN the items list: ↑/↓ on the page and the detail pane's
 * ‹ › (KPR-1643) move the same way. Every fact it reads comes from the page's
 * refs, so the step it returns keeps one identity:
 *
 *   feedRef       { items, selectedItemId }: the items a collapsed group has
 *                 not hidden, and the one the cursor is on
 *   selectRef     { enabled, disabledFor(item) }: the bulk selection's rules
 *   selectionRef  the selection itself (`toggle(id, on)`)
 *
 *   step(delta, extend)   delta 1 down, -1 up; `extend` (Shift) takes the
 *                         rows into the selection on the way
 */
export default function useListStep({ feedRef, selectRef, selectionRef, selectItem }) {
	return useCallback((delta, extend = false) => {
		const { items: navigableNow, selectedItemId: cursorId } = feedRef.current;
		const fromIndex = cursorId
			? navigableNow.findIndex(item => normalizeId(item?._id) === cursorId)
			: -1;
		const from = fromIndex >= 0 ? fromIndex : (delta > 0 ? -1 : 0);
		const nextIndex = Math.min(navigableNow.length - 1, Math.max(0, from + delta));
		const next = navigableNow[nextIndex];
		if (!next) return;
		// Shift extends the selection as the cursor moves, taking the row it
		// leaves AND the row it lands on — the range a shift-arrow means in
		// every list anyone has used.
		if (selectRef.current.enabled && extend) {
			const here = navigableNow[from >= 0 ? from : nextIndex];
			if (here && !selectRef.current.disabledFor(here)) {
				selectionRef.current.toggle(normalizeId(here._id), true);
			}
			if (!selectRef.current.disabledFor(next)) {
				selectionRef.current.toggle(normalizeId(next._id), true);
			}
		}
		selectItem(next);
		document.querySelector(`[data-item-id="${normalizeId(next._id)}"]`)
			?.scrollIntoView({ block: 'nearest' });
	}, [feedRef, selectRef, selectionRef, selectItem]);
}
